'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Filter,
  FilteredCounter,
  LifecycleFilter,
  ListPageFilterPanel,
  type FilterItem,
} from '@terra/ui-design-system';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type { AiComponentListGroupDto } from '@terra/shared-contracts-ai-applications';
import {
  AI_COMPONENT_COVERAGE_FILTERS,
  AI_COMPONENT_INTERFACE_TYPES,
  isAiComponentInterfaceType,
  isAiComponentsCoverageFilter,
  type AiComponentsFilters,
} from './ai-components-table.model';

type AiComponentsFilterPanelProps = {
  filters: AiComponentsFilters;
  onFiltersChange: (filters: AiComponentsFilters) => void;
  roleGroups: readonly AiComponentListGroupDto[];
  isLifecycleEnabled: boolean;
  lifecycleState: LifecycleStateFilterDto;
  onLifecycleStateChange: (lifecycleState: LifecycleStateFilterDto) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
  filteredCount: number;
  totalCount: number;
};

export function AiComponentsFilterPanel({
  filters,
  onFiltersChange,
  roleGroups,
  isLifecycleEnabled,
  lifecycleState,
  onLifecycleStateChange,
  hasActiveFilters,
  onClear,
  filteredCount,
  totalCount,
}: AiComponentsFilterPanelProps) {
  const tAssets = useTranslations('aiApplications.assets');
  const tInterfaceTypes = useTranslations(
    'aiApplications.interfaceTypeOptions',
  );
  const tEntities = useTranslations('proxy.filteredCounter.entities');
  const coverageItems = useMemo(
    (): FilterItem[] =>
      AI_COMPONENT_COVERAGE_FILTERS.map((coverage) => ({
        label: tAssets(coverage),
        value: coverage,
      })),
    [tAssets],
  );
  const roleItems = useMemo(
    (): FilterItem[] =>
      roleGroups.flatMap((group) =>
        group.key ? [{ label: group.key, value: group.key }] : [],
      ),
    [roleGroups],
  );
  const interfaceItems = useMemo(
    (): FilterItem[] =>
      AI_COMPONENT_INTERFACE_TYPES.map((interfaceType) => ({
        label: tInterfaceTypes(interfaceType),
        value: interfaceType,
      })),
    [tInterfaceTypes],
  );

  return (
    <ListPageFilterPanel
      isClearDisabled={!hasActiveFilters}
      onClear={onClear}
      trailing={
        hasActiveFilters ? (
          <FilteredCounter
            filteredCount={filteredCount}
            totalCount={totalCount}
            entityLabel={tEntities('assets')}
          />
        ) : null
      }
    >
      <Filter
        id="ai-component-coverage"
        density="compact"
        placeholder={tAssets('filters.coverage')}
        items={coverageItems}
        value={filters.coverage ? [filters.coverage] : []}
        onChange={(values) =>
          onFiltersChange({
            ...filters,
            coverage: values.find(isAiComponentsCoverageFilter) ?? null,
          })
        }
      />
      <Filter
        id="ai-component-role"
        density="compact"
        placeholder={tAssets('filters.role')}
        items={roleItems}
        disabled={roleItems.length === 0 && !filters.role}
        value={filters.role ? [filters.role] : []}
        onChange={(values) =>
          onFiltersChange({ ...filters, role: values[0] ?? null })
        }
      />
      <Filter
        id="ai-component-interface"
        density="compact"
        placeholder={tAssets('filters.method')}
        items={interfaceItems}
        value={filters.interfaceType ? [filters.interfaceType] : []}
        onChange={(values) =>
          onFiltersChange({
            ...filters,
            interfaceType: values.find(isAiComponentInterfaceType) ?? null,
          })
        }
      />
      {isLifecycleEnabled ? (
        <LifecycleFilter
          value={lifecycleState}
          onValueChange={onLifecycleStateChange}
        />
      ) : null}
    </ListPageFilterPanel>
  );
}
